<script lang="ts" setup>
import { isDefined } from '@scalar/helpers/array/is-defined'
import type { WorkspaceEventBus } from '@scalar/workspace-store/events'
import { resolve } from '@scalar/workspace-store/resolve'
import type { SchemaObject } from '@scalar/workspace-store/schemas/v3.2/strict/openapi-document'
import {
  isArraySchema,
  isNumberSchema,
  isStringSchema,
} from '@scalar/workspace-store/schemas/v3.2/strict/type-guards'
import { computed, toRef } from 'vue'

import { Badge } from '@/components/Badge'
import { useLocalization } from '@/features/localization'

import { getSchemaType } from './helpers/get-schema-type'
import { getDisplayTypeSignatureTokens } from './helpers/get-type-signature-tokens'
import {
  isModelLinkable,
  type ModelLinkOptions,
} from './helpers/is-model-linkable'
import { getModelNameWithArray } from './helpers/schema-name'
import RenderString from './RenderString.vue'
import SchemaPropertyDefault from './SchemaPropertyDefault.vue'
import SchemaPropertyDetail from './SchemaPropertyDetail.vue'
import SchemaPropertyExamples from './SchemaPropertyExamples.vue'
import SchemaPropertyPattern from './SchemaPropertyPattern.vue'

const props = withDefaults(
  defineProps<{
    value: SchemaObject | undefined
    enum?: boolean
    isDiscriminator?: boolean
    required?: boolean
    additional?: boolean
    withExamples?: boolean
    hideModelNames?: boolean
    /**
     * Config for deciding whether a model name links to the models section. When the section or
     * the referenced model is hidden there is nothing to scroll to, so the name renders as plain text.
     */
    modelLinkOptions?: ModelLinkOptions
    /** When the schema was resolved from a $ref, pass the ref name so it displays as e.g. "Data" instead of "object". */
    modelName?: string | null
    /** Resolved propertyNames schema, used to surface key constraints like `format` for additional properties. */
    propertyNames?: SchemaObject
    eventBus?: WorkspaceEventBus | null
    /**
     * The row's name is a stand-in for keys the caller chooses
     * (`additionalProperties`) or keys matching a regex (`patternProperties`),
     * not a literal property. The heading says so in the signature line, where
     * the tree already describes shapes, instead of styling the name itself.
     */
    keyKind?: 'additional' | 'pattern'
    /**
     * The name of the schema this row loops back to. A cut cycle is a leaf, so
     * the signature line says why it does not expand; the model link beside it
     * already names the schema the loop returns to.
     */
    recursiveTo?: string
  }>(),
  {
    isDiscriminator: false,
    required: false,
    withExamples: true,
    hideModelNames: false,
    eventBus: null,
  },
)
const { translate } = useLocalization()

// Convert to reactive refs for composables
const valueRef = toRef(props, 'value')

const constValue = computed(() => {
  if (!valueRef.value) {
    return undefined
  }

  const schema = valueRef.value

  // Direct const value
  if (schema.const !== undefined) {
    return schema.const
  }

  // Single-item enum acts as const
  if (schema.enum?.length === 1) {
    return schema.enum[0]
  }

  // Check items for const values (for arrays)
  if (isArraySchema(schema) && schema.items) {
    const items = resolve.schema(schema.items)

    if (isDefined(items.const)) {
      return items.const
    }

    if (items.enum?.length === 1) {
      return items.enum[0]
    }
  }

  return undefined
})

type ValidationProperty = {
  key: string
  value: string | number
  prefix?: string
  code?: boolean
  truncate?: boolean
}

/**
 * Constraints that live on a leaf (string or number) schema: length, pattern,
 * format and numeric ranges. Extracted so they can be surfaced for a primitive
 * schema as well as for a primitive array's items, which are not rendered on
 * their own. See https://github.com/scalar/scalar/issues/9690
 */
const getLeafConstraints = (schema: SchemaObject) => {
  const properties: ValidationProperty[] = []

  if (isStringSchema(schema)) {
    if (schema.minLength) {
      properties.push({
        key: 'min-length',
        prefix: `${translate('schema.minLength')}: `,
        value: schema.minLength,
      })
    }

    if (schema.maxLength) {
      properties.push({
        key: 'max-length',
        prefix: `${translate('schema.maxLength')}: `,
        value: schema.maxLength,
      })
    }

    // pattern is rendered via SchemaPropertyPattern (hover dropdown), skip here
  }

  if ((isStringSchema(schema) || isNumberSchema(schema)) && schema.format) {
    properties.push({
      key: 'format',
      value: schema.format,
      truncate: true,
    })
  }

  if (isNumberSchema(schema)) {
    if (isDefined(schema.exclusiveMinimum)) {
      properties.push({
        key: 'exclusive-minimum',
        prefix: `${translate('schema.greaterThan')}: `,
        value: schema.exclusiveMinimum,
      })
    }

    if (isDefined(schema.minimum)) {
      properties.push({
        key: 'minimum',
        prefix: `${translate('schema.min')}: `,
        value: schema.minimum,
      })
    }

    if (isDefined(schema.exclusiveMaximum)) {
      properties.push({
        key: 'exclusive-maximum',
        prefix: `${translate('schema.lessThan')}: `,
        value: schema.exclusiveMaximum,
      })
    }

    if (isDefined(schema.maximum)) {
      properties.push({
        key: 'maximum',
        prefix: `${translate('schema.max')}: `,
        value: schema.maximum,
      })
    }

    if (isDefined(schema.multipleOf)) {
      properties.push({
        key: 'multiple-of',
        prefix: `${translate('schema.multipleOf')}: `,
        value: schema.multipleOf,
      })
    }
  }

  return properties
}

const validationProperties = computed(() => {
  if (!valueRef.value) {
    return []
  }

  const schema = valueRef.value
  const properties: ValidationProperty[] = []

  // Array validation properties
  if (isArraySchema(schema)) {
    if (schema.minItems || schema.maxItems) {
      properties.push({
        key: 'array-range',
        value: `${schema.minItems || ''}…${schema.maxItems || ''}`,
      })
    }

    // Unique items
    if (schema.uniqueItems) {
      properties.push({
        key: 'unique-items',
        value: `${translate('schema.unique')}!`,
      })
    }
  }

  properties.push(...getLeafConstraints(schema))

  // Primitive array items carry their own constraints (e.g. an array of `uuid`
  // strings) but are not rendered separately, so surface them here.
  if (isArraySchema(schema) && schema.items) {
    properties.push(...getLeafConstraints(resolve.schema(schema.items)))
  }

  return properties
})

/** Link data for navigating to the referenced model in the sidebar. */
const modelLink = computed(() => {
  if (!props.value) {
    return null
  }

  if (props.hideModelNames) {
    return null
  }

  if (props.modelName) {
    return { schemaKey: props.modelName, label: props.modelName }
  }

  return getModelNameWithArray(props.value)
})

/** Whether the model name links to the models section, or renders as plain text. */
const modelLinkable = computed(() =>
  isModelLinkable(modelLink.value?.schemaKey, props.modelLinkOptions ?? {}),
)

/**
 * The type as a run of tokens rather than a single string — identifiers in
 * the code face, English words like `array of` in the sans face, and a muted
 * `|` so `string | null` reads as one type with an alternative.
 *
 * The token run and the screen-reader label are written straight into the
 * template instead of being their own components: the tree mounts one per
 * typed row, and a component instance costs more to create than the span it
 * renders. On a flat object the type cells would be most of the instance
 * count. The linked and the plain copy of the token markup below must stay
 * identical.
 */
const signatureTokens = computed(() =>
  getDisplayTypeSignatureTokens(props.value, {
    hideModelNames: props.hideModelNames,
    modelName: modelLink.value?.label,
  }),
)

/** Check if we should show the type information */
const shouldShowType = computed(() => {
  if (!props.value || !('type' in props.value)) {
    return false
  }

  // Always show type for arrays, even when items have const values
  if (props.value.type === 'array') {
    return true
  }

  // For non-arrays, only show if no const value at the schema level
  return !constValue.value
})

/**
 * Type and format of the property keys, derived from the propertyNames schema.
 *
 * For a map keyed by UUIDs this renders e.g. "string · uuid" so the key
 * constraints are not lost. Returns undefined when there is nothing to show.
 */
const propertyNamesDetail = computed(() => {
  const schema = props.propertyNames
  if (!schema) {
    return undefined
  }

  const parts = [getSchemaType(schema)]
  if ('format' in schema && typeof schema.format === 'string') {
    parts.push(schema.format)
  }

  const detail = parts.filter(Boolean).join(' · ')
  return detail.length > 0 ? detail : undefined
})

const exampleValue = computed(() => {
  // Treat only `undefined` as "not set" — `null` is a valid example value on a nullable schema.
  if (
    props.value &&
    'example' in props.value &&
    props.value.example !== undefined
  ) {
    return props.value.example
  }

  if (props.value && isArraySchema(props.value)) {
    const itemsSchema = resolve.schema(props.value.items)
    if (
      itemsSchema &&
      'example' in itemsSchema &&
      itemsSchema.example !== undefined
    ) {
      return itemsSchema.example
    }
  }

  return undefined
})

/**
 * Whether the examples chip has anything to render.
 *
 * `SchemaPropertyExamples` already renders nothing without an example, but it
 * still mounts, and mounting is what installs its popup's window-level click
 * and keydown listeners. `withExamples` defaults to true, so without this gate
 * every property row on the page — including a plain `{ type: 'string' }` —
 * pays for a popup it never opens. Mirrors the component's own two branches.
 */
const hasExampleContent = computed((): boolean => {
  if (exampleValue.value !== undefined) {
    return true
  }

  const examples = props.value?.examples

  return (
    !!examples &&
    typeof examples === 'object' &&
    Object.keys(examples).length > 0
  )
})

/**
 * The regex `pattern` to surface via the hover dropdown. It lives on a string
 * schema, or on the items of a primitive array (which is not rendered on its
 * own, so its constraints are surfaced on the array heading — see
 * https://github.com/scalar/scalar/issues/9690).
 */
const patternValue = computed(() => {
  const schema = valueRef.value
  if (!schema) {
    return undefined
  }

  if (isStringSchema(schema) && schema.pattern) {
    return schema.pattern
  }

  if (isArraySchema(schema) && schema.items) {
    const items = resolve.schema(schema.items)
    if (isStringSchema(items) && items.pattern) {
      return items.pattern
    }
  }

  return undefined
})
</script>
<template>
  <div
    class="property-heading [&>.property-detail:has(+.property-detail)]:mr-0">
    <!-- `:has(+.property-detail)`: only a detail FOLLOWED BY another detail
         drops its right margin, because the `·` separator supplies the gap
         there. Not `:not(:last-of-type)`, which matches by element type: the
         collapsed preview and the trailing copy-link are spans as well, so it
         would strip the margin from the final detail and glue "Addressrequired"
         together on a collapsed row. -->
    <div
      v-if="$slots.name"
      class="property-name"
      :class="{ deprecated: props.value?.deprecated }">
      <slot name="name" />
    </div>
    <div
      v-if="props.isDiscriminator"
      class="property-discriminator">
      {{ translate('schema.discriminator') }}
    </div>
    <template v-if="props.value">
      <!-- A map key reads `additionalProperty · string`: the keyword leads the
           detail list in the accent colour, like a type word, so the name
           above it can look like every other name. -->
      <SchemaPropertyDetail
        v-if="props.keyKind"
        class="property-key-kind">
        <span class="font-code text-c-accent">{{
          props.keyKind === 'pattern' ? 'patternProperty' : 'additionalProperty'
        }}</span>
      </SchemaPropertyDetail>
      <!-- Type information -->
      <SchemaPropertyDetail
        v-if="shouldShowType"
        truncate>
        <!-- The type is a token run (`array of Planet`) and a $ref link IS the
             type, not an appended `· Account` -->
        <!-- Written across lines on purpose: Vue drops whitespace-only text
             between two elements, so a single-line label would read
             `Type:string`. -->
        <span class="screenreader-only"> {{ translate('schema.type') }}: </span>
        <button
          v-if="props.eventBus && modelLink?.schemaKey && modelLinkable"
          class="text-c-3 hover:text-c-1 underline"
          type="button"
          @click="
            props.eventBus.emit('scroll-to:model-by-name', {
              name: modelLink.schemaKey,
            })
          ">
          <span
            v-if="signatureTokens.length"
            class="property-type-signature text-c-2 text-(length:--scalar-mini)">
            <template
              v-for="(token, index) in signatureTokens"
              :key="index">
              {{ index > 0 ? ' ' : ''
              }}<span
                class="property-type-token"
                :class="[
                  `property-type-token--${token.kind}`,
                  token.kind === 'word' ? 'font-sans' : '',
                  token.kind === 'ident' || token.kind === 'literal'
                    ? 'font-code'
                    : '',
                  token.kind === 'punctuation' ? 'text-c-3' : '',
                ]"
                >{{ token.text }}</span
              >
            </template>
          </span>
        </button>
        <span
          v-else-if="signatureTokens.length"
          class="property-type-signature text-c-2 text-(length:--scalar-mini)">
          <template
            v-for="(token, index) in signatureTokens"
            :key="index">
            {{ index > 0 ? ' ' : ''
            }}<span
              class="property-type-token"
              :class="[
                `property-type-token--${token.kind}`,
                token.kind === 'word' ? 'font-sans' : '',
                token.kind === 'ident' || token.kind === 'literal'
                  ? 'font-code'
                  : '',
                token.kind === 'punctuation' ? 'text-c-3' : '',
              ]"
              >{{ token.text }}</span
            >
          </template>
        </span>
      </SchemaPropertyDetail>

      <!-- A cycle reads `array of Satellite · recursive`, a modifier like
           `nullable`; the full sentence rides the tooltip. -->
      <SchemaPropertyDetail
        v-if="props.recursiveTo"
        class="property-recursive"
        :title="
          translate('schema.recursiveReference', { name: props.recursiveTo })
        ">
        {{ translate('schema.recursive') }}
      </SchemaPropertyDetail>

      <!-- Key constraints from propertyNames (e.g. "keys: string · uuid") -->
      <SchemaPropertyDetail
        v-if="propertyNamesDetail"
        truncate>
        <template #prefix>{{ translate('schema.keys') }}:</template>
        {{ propertyNamesDetail }}
      </SchemaPropertyDetail>

      <!-- Dynamic validation properties from composable -->
      <SchemaPropertyDetail
        v-for="property in validationProperties"
        :key="property.key"
        :code="property.code"
        :truncate="property.truncate">
        <span
          v-if="property.key === 'format'"
          class="screenreader-only">
          {{ translate('schema.format') }}:
        </span>
        <template
          v-if="property.prefix"
          #prefix>
          {{ property.prefix }}
        </template>
        {{ property.value }}
      </SchemaPropertyDetail>

      <!-- Enum indicator -->
      <SchemaPropertyDetail v-if="props.enum">
        {{ translate('schema.enum') }}
      </SchemaPropertyDetail>
    </template>
    <div
      v-if="props.additional"
      class="property-additional">
      <template v-if="props.value?.['x-additionalPropertiesName']">
        {{ props.value['x-additionalPropertiesName'] }}
      </template>
      <template v-else>{{ translate('schema.additionalProperties') }}</template>
    </div>
    <div
      v-if="props.value?.deprecated"
      class="property-deprecated">
      <Badge>{{ translate('schema.deprecated') }}</Badge>
    </div>
    <!-- Don't use `isDefined` here, we want to show `const` when the value is `null` -->
    <div
      v-if="constValue !== undefined"
      class="property-const">
      <SchemaPropertyDetail truncate>
        <template #prefix>{{ translate('schema.const') }}: </template>
        <RenderString :value="constValue" />
      </SchemaPropertyDetail>
    </div>
    <template v-else>
      <!-- Shows only when a composition is used (so props.value?.type is undefined) -->
      <SchemaPropertyDetail v-if="(props.value as any)?.nullable === true">
        {{ translate('schema.nullable') }}
      </SchemaPropertyDetail>
    </template>
    <div
      v-if="props.value?.writeOnly"
      class="property-write-only">
      {{ translate('schema.writeOnly') }}
    </div>
    <div
      v-else-if="props.value?.readOnly"
      class="property-read-only">
      {{ translate('schema.readOnly') }}
    </div>
    <div
      v-if="props.required"
      class="property-required">
      {{ translate('schema.required') }}
    </div>
    <!-- Gated here, not only inside the component: each popup instance installs
         window-level click and keydown listeners for its dismissal, so mounting
         one per row costs the page a listener per row it never shows anything
         for. Same reason `SchemaPropertyPattern` below is gated. -->
    <SchemaPropertyDefault
      v-if="props.value?.default !== undefined"
      :value="props.value?.default" />
    <!--
      Pattern is a hover dropdown chip like Examples, not an inline constraint,
      so it sits beside the example. This keeps the real constraints (length,
      nullable, …) together in the dotted list. See #9966.
    -->
    <SchemaPropertyPattern
      v-if="patternValue"
      :pattern="patternValue" />
    <SchemaPropertyExamples
      v-if="props.withExamples && hasExampleContent"
      :example="exampleValue"
      :examples="props.value?.examples" />
    <!-- The collapsed preview rides the end of the heading line -->
    <slot name="preview" />
    <!-- Last so the tab order matches the visual order: the trailing copy-link
         is painted at the row's right edge, after every control to its left -->
    <slot name="trailing" />
  </div>
</template>
<style scoped>
.property-heading {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  row-gap: 9px;
  white-space: nowrap;
}

.property-heading:has(+ .children),
.property-heading:has(+ .property-rule) {
  margin-bottom: 9px;
}

.property-heading > * {
  margin-right: 9px;
}

.property-heading:last-child {
  margin-right: 0;
}

.property-name {
  max-width: 100%;
  font-family: var(--scalar-font-code);
  font-weight: var(--scalar-bold);
  font-size: var(--scalar-font-size-4);
  white-space: normal;
  overflow-wrap: break-word;
}

.property-additional {
  font-family: var(--scalar-font-code);
}

.property-required,
.property-optional {
  color: var(--scalar-color-2);
}

.property-required {
  font-size: var(--scalar-mini);
  color: var(--scalar-color-orange);
}

.property-read-only {
  font-size: var(--scalar-mini);
  color: var(--scalar-color-blue);
}

.property-write-only {
  font-size: var(--scalar-mini);
  color: var(--scalar-color-green);
}

.property-discriminator {
  font-size: var(--scalar-mini);
  color: var(--scalar-color-purple);
}

.property-detail {
  font-size: var(--scalar-mini);
  color: var(--scalar-color-2);
  display: flex;
  align-items: center;

  min-width: 0;
}

.property-const {
  color: var(--scalar-color-1);
}

.deprecated {
  text-decoration: line-through;
}
</style>
